import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Button, Input, RadioGroup, Switch, Text, Textarea, toast } from "@medusajs/ui"
import { ErrorState, Section } from "@/components/ui"
import { useCan } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"
import { usePaymentSettings, useSavePaymentSettings, type PaymentSettings, type WalletInput, type WalletStatus } from "@/lib/settings"
import { Badge, DefinitionList } from "@/terabizz"
import { SettingsTitle } from "./settings-general"

/**
 * Settings → Payments. The gateway keys live in AWS Secrets Manager (never in the
 * admin); this page shows the connection and sets the capture mode, like Shopify.
 */
export function PaymentsSettingsPage() {
  const { data, error } = usePaymentSettings()
  const save = useSavePaymentSettings()
  const writable = useCan()("settings:write")
  if (error) return <ErrorState error={error} />
  if (!data) return <Text size="small">Loading…</Text>
  const g = data.gateway
  const set = (capture: PaymentSettings["capture"]) =>
    save.mutate({ capture }, { onSuccess: () => toast.success("Payment capture updated"), onError: (e) => toast.error(errorMessage(e)) })
  return (
    <div className="flex flex-col gap-4 pb-24">
      <SettingsTitle title="Payments" />
      <Section title="Payment provider">
        <div className="flex items-start justify-between gap-4 p-4">
          <div>
            <Text size="small" weight="plus">{g.provider}</Text>
            <Text size="small" className="mt-1 text-ui-fg-subtle">Credit and debit cards (Visa, Mastercard, American Express, Discover, JCB). Card details are typed into Authorize.net’s secure form and never reach this store.</Text>
          </div>
          {g.connected ? (
            <Badge tone={g.environment === "production" ? "success" : "attention"}>{g.environment === "production" ? "Active" : "Test mode"}</Badge>
          ) : (
            <Badge tone="critical">Not connected</Badge>
          )}
        </div>
        <DefinitionList
          rows={[
            ["Environment", g.environment === "production" ? "Live" : "Sandbox (test cards only)"],
            ["API login ID", g.login_id ?? "—"],
            ["Hosted card form", g.hosted_form_ready ? "Ready" : "Missing public client key"],
          ]}
        />
        {!g.connected || g.environment !== "production" ? (
          <Text size="xsmall" className="px-4 pb-4 text-ui-fg-subtle">
            Keys are set by the hosting team in AWS Secrets Manager (AUTHNET_API_LOGIN_ID, AUTHNET_TRANSACTION_KEY, AUTHNET_CLIENT_KEY, AUTHNET_ENV=production). They are never shown or edited here.
          </Text>
        ) : null}
      </Section>
      <Section title="Payment capture">
        <div className="p-4">
          <Text size="small" className="mb-3 text-ui-fg-subtle">When an order is placed, the card is authorized. Capture is when the money is actually taken.</Text>
          <RadioGroup value={data.settings.capture} onValueChange={(v) => set(v as PaymentSettings["capture"])} disabled={!writable || save.isPending} className="flex flex-col gap-3">
            <label className="flex items-start gap-3">
              <RadioGroup.Item value="automatic" id="cap-auto" className="mt-0.5" />
              <span>
                <Text size="small" weight="plus">Automatically at checkout</Text>
                <Text size="small" className="text-ui-fg-subtle">Capture the payment when the order is placed.</Text>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <RadioGroup.Item value="manual" id="cap-manual" className="mt-0.5" />
              <span>
                <Text size="small" weight="plus">Manually</Text>
                <Text size="small" className="text-ui-fg-subtle">Capture from the order page (for example when you ship). Authorizations usually expire after 30 days.</Text>
              </span>
            </label>
          </RadioGroup>
        </div>
      </Section>
      <WalletsSection wallets={data.wallets} environment={g.environment} writable={writable} saving={save.isPending} onSave={(wallets, done) => save.mutate({ wallets }, { onSuccess: () => (toast.success("Wallets updated"), done?.()), onError: (e) => toast.error(errorMessage(e)) })} />
      <Section title="Refunds and cancellations">
        <ul className="flex list-disc flex-col gap-1.5 p-4 pl-8 text-[13px] text-ui-fg-subtle">
          <li>Canceling an unpaid (authorized) order releases the hold on the customer’s card.</li>
          <li>Before the payment settles (same day), only a full refund is possible; it voids the charge.</li>
          <li>After it settles (usually the next day), you can refund any amount.</li>
          <li>Orders flagged by Authorize.net’s fraud filters are placed but held; approve them in Authorize.net before capturing.</li>
        </ul>
      </Section>
    </div>
  )
}

/**
 * Apple Pay and Google Pay (backend lib/wallets.ts), paid through Authorize.net. Shown at checkout
 * for one-time orders only (a wallet payment can't be saved for subscription renewals), and only
 * once each wallet's setup is complete; this card says what is still missing.
 */
function WalletsSection({ wallets, environment, writable, saving, onSave }: { wallets: WalletStatus; environment: "sandbox" | "production"; writable: boolean; saving: boolean; onSave: (w: WalletInput, done?: () => void) => void }) {
  const s = wallets.settings
  const [merchantId, setMerchantId] = useState(s.google_merchant_id)
  const [domainFile, setDomainFile] = useState("")
  const status = (on: boolean, ready: boolean) => (!on ? <Badge tone="neutral">Off</Badge> : ready ? <Badge tone="success">On at checkout</Badge> : <Badge tone="attention">Setup incomplete</Badge>)
  return (
    <Section title="Wallets">
      <Text size="small" className="px-4 pt-4 text-ui-fg-subtle">Express buttons at the top of the payment step, for one-time orders. Subscriptions are always paid by card (wallet payments can’t be saved for renewals).</Text>
      <div className="flex flex-col gap-3 p-4">
        <div className="rounded-lg border border-ui-border-base p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Switch checked={s.google_pay} disabled={!writable || saving} onCheckedChange={(v) => onSave({ google_pay: v })} aria-label="Google Pay" />
              <Text size="small" weight="plus">Google Pay</Text>
            </div>
            {status(s.google_pay, wallets.google_pay.ready)}
          </div>
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-[13px] text-ui-fg-subtle">
            <li>{wallets.google_pay.gateway_id_found ? "Authorize.net gateway ID found." : "Authorize.net gateway ID not found yet (the payment keys must be connected)."}</li>
            <li>{environment === "production" ? (s.google_merchant_id ? `Google Pay merchant ID: ${s.google_merchant_id}` : "Live payments need your Google Pay merchant ID (Google Pay & Wallet Console → Business profile).") : "Test mode: Google shows test cards; no merchant ID needed."}</li>
          </ul>
          {writable ? (
            <div className="mt-3 flex items-end gap-2">
              <div className="flex-1">
                <Text size="xsmall" className="mb-1 text-ui-fg-subtle">Google Pay merchant ID</Text>
                <Input size="small" value={merchantId} onChange={(e) => setMerchantId(e.target.value.trim())} placeholder="BCR2DN4T…" />
              </div>
              <Button size="small" variant="secondary" disabled={saving || merchantId === s.google_merchant_id} onClick={() => onSave({ google_merchant_id: merchantId })}>Save</Button>
            </div>
          ) : null}
        </div>
        <div className="rounded-lg border border-ui-border-base p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Switch checked={s.apple_pay} disabled={!writable || saving} onCheckedChange={(v) => onSave({ apple_pay: v })} aria-label="Apple Pay" />
              <Text size="small" weight="plus">Apple Pay</Text>
            </div>
            {status(s.apple_pay, wallets.apple_pay.ready)}
          </div>
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-[13px] text-ui-fg-subtle">
            <li>
              {wallets.apple_pay.certificate_set
                ? `Apple merchant ID ${wallets.apple_pay.merchant_id}: merchant identity certificate set${wallets.apple_pay.setup?.certificate?.expires_at && wallets.apple_pay.source === "settings" ? ` (expires ${new Date(wallets.apple_pay.setup.certificate.expires_at).toLocaleDateString()})` : ""}.`
                : "Merchant identity certificate not set yet: steps below."}
            </li>
            <li>{wallets.apple_pay.domain_file_set ? "Domain verification file saved (served at /.well-known/apple-developer-merchantid-domain-association)." : "Paste the domain verification file from Apple below, then click Verify in Apple’s portal."}</li>
            <li>Apple Pay must also be switched on in Authorize.net (Account → Digital Payment Solutions), using Authorize.net’s certificate request.</li>
          </ul>
          {writable && wallets.apple_pay.source !== "env" ? <AppleIdentitySetup wallets={wallets} /> : null}
          {writable ? (
            <div className="mt-3 flex flex-col gap-2">
              <Text size="xsmall" className="text-ui-fg-subtle">Domain verification file (open the file Apple gives you in a text editor and paste all of it)</Text>
              <Textarea rows={3} value={domainFile} onChange={(e) => setDomainFile(e.target.value)} placeholder={s.apple_domain_file_set ? "Saved. Paste a new file to replace it." : "7B227073704964223A2…"} />
              <div>
                <Button size="small" variant="secondary" disabled={saving || !domainFile.trim()} onClick={() => onSave({ apple_domain_file: domainFile.trim() }, () => setDomainFile(""))}>Save file</Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </Section>
  )
}

/**
 * Apple Pay merchant identity certificate, set up here (backend lib/apple-pay-identity.ts):
 * 1) merchant ID → download the certificate request, 2) upload it in Apple's portal
 * (Merchant ID → Apple Pay Merchant Identity Certificate), 3) upload Apple's .cer here.
 * The private key is made and kept (encrypted) on the server.
 */
function AppleIdentitySetup({ wallets }: { wallets: WalletStatus }) {
  const qc = useQueryClient()
  const setup = wallets.apple_pay.setup
  const [merchantId, setMerchantId] = useState(setup?.merchant_id ?? "")
  const [busy, setBusy] = useState(false)
  const valid = /^merchant\.[A-Za-z0-9.-]{3,120}$/.test(merchantId.trim())
  const base = import.meta.env.VITE_MEDUSA_BACKEND_URL || window.location.origin

  async function downloadCsr() {
    setBusy(true)
    try {
      const res = await fetch(`${base}/admin/terify/settings/payments/apple-pay/csr`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ merchant_id: merchantId.trim() }) })
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.message ?? "Couldn't create the request")
      const url = URL.createObjectURL(await res.blob())
      const a = Object.assign(document.createElement("a"), { href: url, download: `${merchantId.trim()}.certSigningRequest` })
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      toast.success("Certificate request downloaded. Upload it in Apple's portal.")
      qc.invalidateQueries({ queryKey: ["settings", "payments"] })
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function uploadCer(file: File) {
    setBusy(true)
    try {
      const bytes = new Uint8Array(await file.arrayBuffer())
      const text = new TextDecoder().decode(bytes)
      const certificate = text.includes("-----BEGIN") ? text : btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(""))
      const r = await sdk.client.fetch<{ wallets: WalletStatus }>("/admin/terify/settings/payments/apple-pay/certificate", { method: "POST", body: { certificate } })
      qc.setQueryData(["settings", "payments"], (old: any) => (old ? { ...old, wallets: r.wallets } : old))
      toast.success("Apple certificate saved")
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-3 flex flex-col gap-2 rounded-md bg-ui-bg-subtle p-3">
      <Text size="xsmall" weight="plus">Merchant identity certificate</Text>
      <ol className="flex list-decimal flex-col gap-1 pl-5 text-[12.5px] text-ui-fg-subtle">
        <li>Enter the merchant ID you created in Apple's portal (Certificates, Identifiers &amp; Profiles → Identifiers → Merchant IDs).</li>
        <li>Download the certificate request and upload it in Apple's portal under that merchant ID → Apple Pay Merchant Identity Certificate → Create Certificate.</li>
        <li>Download the certificate Apple makes (merchant_id.cer) and upload it here.</li>
      </ol>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[240px] flex-1">
          <Input value={merchantId} placeholder="merchant.com.tolvanelabs" onChange={(e) => setMerchantId(e.target.value)} aria-label="Apple merchant ID" />
        </div>
        <Button size="small" variant="secondary" disabled={!valid || busy} onClick={downloadCsr}>
          {setup?.csr_created_at ? "Download a new request" : "Download certificate request"}
        </Button>
      </div>
      {setup?.csr_created_at && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex">
            <input type="file" accept=".cer,.pem,.crt" className="hidden" disabled={busy} onChange={(e) => e.target.files?.[0] && uploadCer(e.target.files[0]).finally(() => (e.target.value = ""))} />
            <span className="cursor-pointer rounded-md border border-ui-border-base bg-ui-bg-base px-3 py-1.5 text-[13px] font-medium shadow-sm hover:bg-ui-bg-base-hover">{setup.certificate ? "Replace Apple certificate" : "Upload Apple certificate (.cer)"}</span>
          </label>
          <Text size="xsmall" className="text-ui-fg-subtle">Request made {new Date(setup.csr_created_at).toLocaleString()}. A new request replaces the old one and its certificate.</Text>
        </div>
      )}
    </div>
  )
}

